import * as React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { LineChart } from '@mui/x-charts/LineChart';
import { useSelector } from 'react-redux';
import { getFilterConfig } from '../../helpers/filterLabelGenerator';

const brlCurrencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
});

const brlCompactFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  notation: 'compact',
  maximumFractionDigits: 1,
});

function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

AreaGradient.propTypes = {
  color: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

function getStatusColorById(theme) {
  return {
    'waiting-analysis': (theme.vars || theme).palette.warning.main,
    analysis: (theme.vars || theme).palette.error.main,
    report: theme.palette.mode === 'light' ? '#7E57C2' : '#B39DDB',
    finalized: (theme.vars || theme).palette.success.main,
  };
}

function TransactionSessionsChart({ title, description, variation, value, labels: propLabels, transactions: propTransactions }) {
  const theme = useTheme();
  const filter = useSelector((state) => state.filter.value);
  const config = getFilterConfig(filter);
  const statusColorById = getStatusColorById(theme);

  const defaultTransactions = [
    {
      id: 'waiting-analysis',
      label: 'Aguardando análise',
      showMark: false,
      curve: 'linear',
      stack: 'total',
      area: true,
      stackOrder: 'ascending',
      data: [
        30000, 90000, 60000, 120000, 150000, 180000, 240000, 210000, 270000, 300000, 180000, 330000,
        360000, 390000, 420000, 450000, 390000, 480000, 510000, 540000, 480000, 570000, 600000,
        630000, 660000, 690000, 720000, 750000, 780000, 810000,
      ],
    },
    {
      id: 'analysis',
      label: 'Em análise',
      showMark: false,
      curve: 'linear',
      stack: 'total',
      area: true,
      stackOrder: 'ascending',
      data: [
        50000, 90000, 70000, 140000, 110000, 170000, 230000, 200000, 260000, 290000, 230000, 320000,
        350000, 380000, 410000, 440000, 290000, 470000, 500000, 530000, 560000, 590000, 620000,
        650000, 560000, 680000, 710000, 740000, 770000, 800000,
      ],
    },
    {
      id: 'report',
      label: 'Reportadas',
      showMark: false,
      curve: 'linear',
      stack: 'total',
      stackOrder: 'ascending',
      area: true,
      data: [
        100000, 150000, 120000, 170000, 130000, 200000, 240000, 220000, 260000, 280000, 2500,
        300000, 340000, 370000, 320000, 390000, 410000, 350000, 430000, 450000, 400000, 4700,
        500000, 520000, 480000, 540000, 560000, 590000, 610000, 630000,
      ],
    },
        {
      id: 'finalized',
      label: 'Finalizadas',
      showMark: false,
      curve: 'linear',
      stack: 'total',
      stackOrder: 'ascending',
      area: true,
      data: [
        150000, 250000, 220000, 270000, 230000, 300000, 340000, 320000, 360000, 380000, 3500,
        400000, 450000, 470000, 320000, 560000, 510000, 400000, 530000, 550000, 500000, 5700,
        600000, 620000, 680000, 640000, 660000, 690000, 710000, 770000,
      ],
    },
  ];

  const labels = Array.isArray(propLabels) && propLabels.length
    ? propLabels
    : config.labels;

  const datumSource = Array.isArray(propTransactions) && propTransactions.length
    ? propTransactions
    : defaultTransactions;

  const datum = datumSource.map((transaction, index) => {
    const fallback = defaultTransactions[index] || defaultTransactions[0];
    const sourceData = Array.isArray(transaction.amount) && transaction.amount.length
      ? transaction.amount
      : transaction.quantity;
    const normalizedData = Array.from({ length: labels.length }, (_, itemIndex) => (
      sourceData && sourceData[itemIndex] !== undefined
        ? sourceData[itemIndex]
        : sourceData && sourceData.length
          ? sourceData[sourceData.length - 1]
          : 0
    ));

    return {
      id: transaction.id || fallback.id,
      label: transaction.label || fallback.label,
      showMark: false,
      curve: 'linear',
      stack: 'total',
      area: true,
      stackOrder: 'ascending',
      data: normalizedData,
      valueFormatter: (item) => brlCurrencyFormatter.format(Number(item?.value ?? item ?? 0)),
    };
  });

  const colorPalette = datum.map(
    (transaction) => statusColorById[transaction.id] || (theme.vars || theme).palette.primary.main,
  );

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        <Stack sx={{ justifyContent: 'space-between' }}>
          <Stack
            direction="row"
            sx={{
              alignContent: { xs: 'center', sm: 'flex-start' },
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="h4" component="p">
              {value}
            </Typography>
            <Chip size="small" color="success" label={variation} />
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {description}
          </Typography>
        </Stack>
        <LineChart
          colors={colorPalette}
          xAxis={[
            {
              scaleType: 'point',
              data: labels,
              // tickInterval: (index, i) => (i + 1) % 5 === 0,
              height: 24,
            },
          ]}
          yAxis={[{ width: 70, valueFormatter: (item) => brlCompactFormatter.format(Number(item ?? 0)) }]}
          series={datum}
          height={250}
          margin={{ left: 0, right: 20, top: 20, bottom: 0 }}
          grid={{ horizontal: true }}
          sx={{
            '& .MuiAreaElement-series-waiting-analysis': {
              fill: "url('#waiting-analysis')",
            },
            '& .MuiAreaElement-series-analysis': {
              fill: "url('#analysis')",
            },
            '& .MuiAreaElement-series-report': {
              fill: "url('#report')",
            },
            '& .MuiAreaElement-series-finalized': {
              fill: "url('#finalized')",
            },
          }}
        // hideLegend
        >
          <AreaGradient color={statusColorById['waiting-analysis']} id="waiting-analysis" />
          <AreaGradient color={statusColorById.analysis} id="analysis" />
          <AreaGradient color={statusColorById.report} id="report" />
          <AreaGradient color={statusColorById.finalized} id="finalized" />
        </LineChart>
      </CardContent>
    </Card>
  );
};

TransactionSessionsChart.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  variation: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string),
  transactions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    quantity: PropTypes.arrayOf(PropTypes.number),
    amount: PropTypes.arrayOf(PropTypes.number),
  })),
}

export default TransactionSessionsChart;
